// The eval case inbox API's shapes (cloud/specs/api_contracts.md §11.7;
// TOM-140, ADR 0233), as the console reads them. The server's own types are
// in cloud/src/core/neo/eval-inbox.ts and eval/inbox-contract.ts; the web
// package does not import server code, so these mirror them. Keep the two in
// step: a field added there is added here. Dates arrive as ISO strings.

export const EVAL_CANDIDATE_SOURCES = [
  'thumbs_down',
  'output_gate',
  'review_tag',
  'input_refusal',
  'gate_disagreement',
  'promoted',
] as const;
export type EvalCandidateSource = (typeof EVAL_CANDIDATE_SOURCES)[number];

/** The input gate's questions (cloud/src/core/neo/input-gate.ts), in its order. */
export const EVAL_GATE_QUESTIONS = [
  'abusive',
  'overrides_instructions',
  'extracts_secrets',
  'harmful',
  'unsafe_maritime',
] as const;
export type EvalGateQuestion = (typeof EVAL_GATE_QUESTIONS)[number];

export type GateOutcome = 'allow' | 'safety' | 'security';

/**
 * A gate disagreement's outcomes, the Jev route that answered and its five
 * scores (TOM-314). Never the owner's message: Neo → Gate shows it, audited.
 */
export interface EvalGateDisagreementInfo {
  id: string;
  createdAt: string;
  decidedBy: 'typesafe' | 'openrouter';
  jevOutcome: GateOutcome;
  regexOutcome: GateOutcome;
  jevScores: Record<string, number>;
  gateVersion: string;
}

export const EVAL_TRIAGE_STATES = [
  'new',
  'drafting',
  'accepted',
  'rejected',
  'duplicate',
  'in_corpus',
] as const;
export type EvalTriageState = (typeof EVAL_TRIAGE_STATES)[number];

export const EVAL_CASE_KINDS = ['attack', 'helpful', 'owner_question'] as const;
export type EvalCaseKind = (typeof EVAL_CASE_KINDS)[number];

export const EVAL_CASE_EXPECTS = ['refuse', 'allow', 'local-answer', 'model'] as const;
export type EvalCaseExpect = (typeof EVAL_CASE_EXPECTS)[number];

/**
 * Which agent should get a case right (TOM-533, ADR 0342 §9): Neo (`chat`) or
 * an expert behind it, in the contract's order (EVAL_CASE_TARGET_AGENTS).
 */
export const EVAL_CASE_TARGET_AGENTS = [
  'chat',
  'boards',
  'electrical',
  'mechanical',
  'electronics',
  'navigation',
] as const;
export type EvalCaseTargetAgent = (typeof EVAL_CASE_TARGET_AGENTS)[number];

export type EvalSourceCounts = Record<EvalCandidateSource, number>;

export type EvalCaseData =
  | { source: 'stub_boat'; variant?: 'noPositions' }
  | { source: 'moment'; boat: string; at: string };

export interface EvalCaseClaims {
  must: string[];
  mustNot: string[];
}

/** When a stored eval run first held the case, and its latest outcome. */
export interface EvalCandidateInCorpus {
  firstRunId: string;
  firstCollectedAt: string;
  results: number;
  latest: {
    runId: string;
    model: string;
    suite: 'short' | 'long';
    collectedAt: string;
    verdictSource: string | null;
    met: boolean | null;
    declined: boolean | null;
    localDecision: 'refused' | 'answered' | null;
    blocked: string | null;
    errored: boolean;
  };
}

/** One queue row. Content-free: enumerated details and ids, never chat text. */
export interface EvalCandidateSummary {
  key: string;
  rowId: string | null;
  state: EvalTriageState;
  sources: EvalCandidateSource[];
  signals: EvalSourceCounts;
  details: {
    feedbackReasons: string[];
    outputGates: string[];
    reviewTags: string[];
    refusalReasons: string[];
    /** Each disagreement's outcomes, Jev's then the regex's: `allow/security`. */
    gateOutcomes: string[];
    promotedCaseTypes: string[];
  };
  promptVersion: string | null;
  /** The experts that took part in the turn (ids); null when none was recorded. */
  specialists: string[] | null;
  /** A Try it guest's turn, refusal or disagreement (TOM-950, ADR 0457). */
  tryIt: boolean;
  messageId: string | null;
  conversationId: string | null;
  refusalId: string | null;
  gateDisagreementId: string | null;
  gate: EvalGateDisagreementInfo | null;
  occurredAt: string | null;
  lastActivityAt: string;
  caseId: string | null;
  kind: string | null;
  category: string | null;
  dataSource: 'stub_boat' | 'moment' | null;
  split: 'train' | 'test' | null;
  /** The agent the reviewer chose, as stored: null when none was, which is Neo. */
  targetAgent: string | null;
  exportedAt: string | null;
  triagedAt: string | null;
  inCorpus: EvalCandidateInCorpus | null;
}

export interface EvalCandidateCounts {
  candidates: number;
  bySource: EvalSourceCounts;
  signalsBySource: EvalSourceCounts;
  byPromptVersion: Array<{
    promptVersion: string | null;
    candidates: number;
    bySource: EvalSourceCounts;
  }>;
  byState: Record<EvalTriageState, number>;
}

export interface EvalCandidateList {
  windowDays: number;
  counts: EvalCandidateCounts;
  candidates: EvalCandidateSummary[];
  nextCursor: string | null;
}

/** The reviewer's draft as stored: every field may still be missing. */
export interface EvalStoredDraft {
  caseId: string | null;
  prompt: string | null;
  kind: string | null;
  category: string | null;
  claims: EvalCaseClaims | null;
  data: EvalCaseData | null;
  expect: string | null;
  expectedTools: string[] | null;
  gateQuestions: string[] | null;
  note: string | null;
  targetAgent: string | null;
}

export type EvalMomentOptionReason =
  | 'not_a_turn'
  | 'turn_erased'
  | 'no_boat'
  | 'no_consent'
  | 'lookup_failed';

export type EvalMomentOption =
  | { offered: true; boat: string; at: string; momentId: string; windowHeld: boolean }
  | { offered: false; reason: EvalMomentOptionReason };

export interface EvalCandidateDetail extends EvalCandidateSummary {
  draft: EvalStoredDraft;
  draftIssues: string[];
  routeProblem: string | null;
  promote: { caseType: string | null; expected: string | null; notes: string | null } | null;
  rejectReason: string | null;
  duplicateOf: { rowId: string; key: string | null } | { caseId: string } | null;
  momentOption: EvalMomentOption;
  /** A short case given an expert: its split among the expert's own questions (TOM-533). */
  expertSplit: 'train' | 'test' | null;
  allowedMoves: EvalTriageState[];
}

/** PUT .../draft: the whole form, every field optional (the contract's draft without caseId). */
export interface EvalDraftSaveBody {
  slug?: string;
  prompt?: string;
  kind?: EvalCaseKind;
  category?: string;
  claims?: EvalCaseClaims;
  data?: EvalCaseData;
  expect?: EvalCaseExpect;
  expectedTools?: string[];
  gateQuestions?: EvalGateQuestion[];
  note?: string;
  targetAgent?: EvalCaseTargetAgent;
}

/** The contract's complete draft, as an accept carries it. A gate case has no claims. */
export interface EvalCaseDraft {
  caseId: string;
  kind: EvalCaseKind;
  category: string;
  prompt: string;
  claims?: EvalCaseClaims;
  data: EvalCaseData;
  expect?: EvalCaseExpect;
  expectedTools?: string[];
  gateQuestions?: EvalGateQuestion[];
  note?: string;
  targetAgent?: EvalCaseTargetAgent;
}

export type EvalTriageDecision =
  | { to: 'new' }
  | { to: 'drafting' }
  | { to: 'accepted'; draft: EvalCaseDraft }
  | { to: 'rejected'; reason: string }
  | { to: 'duplicate'; of: { caseId: string } | { candidate: string } };

export interface EvalDraftSaveOutcome {
  rowId: string;
  key: string;
  caseId: string;
  from: EvalTriageState;
}

export interface EvalTriageOutcome {
  rowId: string;
  key: string;
  from: EvalTriageState;
  to: EvalTriageState;
  caseId: string | null;
}

export interface EvalCaseLeftOut {
  caseId: string;
  reason: string;
}

/** The version-2 export file (`tombolo.neo-eval-cases`). Cases are kept opaque here. */
export interface EvalCaseExportFile {
  format: 'tombolo.neo-eval-cases';
  version: 2;
  exportedAt: string;
  deployEnv: 'development' | 'staging' | 'production';
  cases: Array<{ caseId: string } & Record<string, unknown>>;
}

export interface EvalCaseExportOutcome {
  file: EvalCaseExportFile;
  leftOut: EvalCaseLeftOut[];
  firstExported: string[];
}

export type EvalConsentRevokeReason = 'withdrawn' | 'account_deleted' | 'boat_deleted';

export interface EvalMomentConsent {
  id: string;
  /** Null once the boat is deleted (ADR 0253). */
  boatId: string | null;
  /** Null once the owner's account is deleted (ADR 0253). */
  ownerUserId: string | null;
  corpusKey: string;
  basis: string;
  grantedBy: string | null;
  grantedAt: string;
  revokedAt: string | null;
  revokedBy: string | null;
  revokedReason: EvalConsentRevokeReason | null;
  active: boolean;
}

export interface EvalConsentRevokeOutcome {
  consent: EvalMomentConsent;
  committed: { momentIds: string[]; caseIds: string[] };
  inboxCaseIds: string[];
}

/** The rows of GET /v1/admin/boats and GET /v1/admin/users the consent form reads. */
export interface AdminBoatRow {
  id: string;
  display_name: string | null;
  decommissioned_at: string | null;
  owner_emails: string[];
}

export interface AdminUserRow {
  id: string;
  email: string;
  boat_count: number;
}
